import React, { useEffect, useState } from 'react'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import copy from 'copy-to-clipboard'
import clsx from 'clsx'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useMediaQuery,
  Container,
  CircularProgress,
  Typography,
} from '@material-ui/core'
import {
  useGlobalSnackbar,
  SnackbarType,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { format } from 'date-fns-next'
import GlobalAlert from '@src/components/GlobalAlert'
import CopyIcon from '@src/icons/CopyIcon'
import InfoIcon from '@material-ui/icons/Info'
import { parseBarcode } from '@src/utils/barcodeParser'
import { countryNames } from './CountryList'
import ScanNowAiModal from './ScanNowAiModal'
import ScanNowMaterialInfo from './ScanNowMaterialInfo'
import {
  useCertificateContentSearchLazyQuery,
  useCertificateContentSearchQuery,
} from '@src/queries/CertificateContentSearchQuery.generated'
import { useEmproveProductDocsLazyQuery } from '@src/queries/EmproveProductDocsQuery.generated'
import { useGtinMaterialsSearchLazyQuery } from '@src/queries/GTINMaterialQuery.generated'
import { useProductDetailLazyQuery } from '@src/queries/ProductDetailQuery.generated'
import { useMultiplePricingAndAvailabilityLazyQuery } from '@src/queries/MultiplePricingAndAvailabilityQuery.generated'
import ScanNowLinks from './ScanNowLinks'
import { useUserSession } from '@src/utils/useUserSession'
import getConfig from 'next/config'
import { Linq } from '@src/components/Linq'
import { documentsSearch } from '@src/routes'
import { QmDocumentType } from '@src/utils/analytics/enums'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& > div:first-of-type': {
      flex: 1,
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyContent: 'space-around',
    },
  },
  table: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '620px',
    },
    '& .MuiTableCell-root': {
      padding: `${theme.spacing(3)}px`,
    },
  },
  tableHead: {
    '& .MuiTableCell-head': {
      backgroundColor: theme.palette.background.grey,
    },
  },
  copyIcon: {
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(16),
  },
  aiContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    fontSize: theme.typography.pxToRem(16),
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  copiedtext: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  copyNote: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.grey[500],
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& .loadingSpinner': {
      margin: theme.spacing(4, 2),
    },
  },
  result: {
    textTransform: 'uppercase',
  },
  errorAlert: {
    width: '100%',
    maxWidth: '620px',
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(10),
  },
}))

interface ScanNowDetailProps {
  barcode: string
}

const ScanNowDetail: React.FC<ScanNowDetailProps> = ({ barcode }) => {
  const { formatMessage } = useIntl()
  const classes = useStyles()
  const theme = useTheme()
  const { setGlobalSnackbarState } = useGlobalSnackbar()
  const [parsedValue, setParsedValue] = useState([])
  const [isAiInfoOpen, setAiInfoOpen] = useState<boolean>(false)
  const [isAI240Missing, setAI240Missing] = useState(false)
  const [copiedAI, setCopiedAI] = useState<string>('')
  const [lotNumber, setLotNumber] = useState<string>('')
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const [additionalId, setAdditionalId] = useState<string>('')
  const { userSession } = useUserSession()

  const {
    publicRuntimeConfig: {
      featureFlags: { blueDocsManagement },
    },
  } = getConfig()

  enum AI {
    AI_10 = '10',
    AI_17 = '17',
    AI_240 = '240',
    AI_422 = '422',
  }

  const [getGTIN, { data: materialsData, loading: materialsLoading }] =
    useGtinMaterialsSearchLazyQuery({ fetchPolicy: 'network-only' })
  const material = materialsData?.getGTINMaterials
  const [
    getCertificateContent,
    { data: contentData, loading: contentLoading },
  ] = useCertificateContentSearchLazyQuery()

  const { data: certificateContentData, loading: certificateContentLoading } =
    useCertificateContentSearchQuery({
      variables: {
        input: {
          brand: material?.brand?.key,
          documentType: QmDocumentType.CofAnalysisDoc,
          productNumber: material?.product || '',
        },
      },
    })

  const certificateContentType =
    certificateContentData?.getCertificateContent?.documentType

  const [getPricingForProduct, { data: materialPricingData }] =
    useMultiplePricingAndAvailabilityLazyQuery()

  const [getProductDetail, { data: productDetailData }] =
    useProductDetailLazyQuery()
  useEffect(() => {
    if (material?.product) {
      getProductDetail({
        variables: {
          productKey: material?.product as string,
          brandKey: material?.brand.key.toLowerCase() as string,
        },
        context: { userSession },
      })
    }
    if (!materialsLoading && material?.number && parsedValue?.length) {
      const ai240: any =
        parsedValue?.find((item: any) => item?.ai === AI.AI_240) || {}
      const ai10: any =
        parsedValue?.find((item: any) => item?.ai === AI.AI_10) || {}
      const ai10HoldMaterialNumber = ai10?.data?.indexOf(material?.number)
      const ai10ActualValue =
        ai10HoldMaterialNumber !== -1
          ? ai10?.data?.slice(0, ai10HoldMaterialNumber - 3)
          : ai10?.data
      let updateParsedValue: any = []

      const ai240Object = {
        data: material?.number,
        ai: '240',
        dataTitle: 'ADDITIONAL ID',
      }
      if (ai240?.data !== material?.number && ai10HoldMaterialNumber !== -1) {
        updateParsedValue = parsedValue.map((item: any) => {
          if (item.ai === AI.AI_10) {
            return {
              ...item,
              data: ai10ActualValue,
            }
          }
          return {
            ...item,
          }
        })
        const updateBarCode: any = [...updateParsedValue, ai240Object]
        setParsedValue(updateBarCode)
        setLotNumber(ai10ActualValue)
        setAI240Missing(true)
      } else if (ai240?.ai === AI.AI_240 && ai240?.data !== material?.number) {
        updateParsedValue = parsedValue?.map((item: any) => {
          if (item.ai === AI.AI_240) {
            return {
              ...item,
              data: material?.number,
            }
          }
          return {
            ...item,
          }
        })
        setParsedValue(updateParsedValue)
      } else if (ai240?.ai !== AI.AI_240) {
        const updateBarCode: any = [...parsedValue, ai240Object]
        setParsedValue(updateBarCode)
        setLotNumber(ai10ActualValue)
        setAI240Missing(true)
      }
    }
  }, [material])

  const [getEmproveProductsDocs, { data: emproveDocumentsData }] =
    useEmproveProductDocsLazyQuery()

  useEffect(() => {
    if (certificateContentType === null) {
      getCertificateContent({
        variables: {
          input: {
            brand: material?.brand?.key,
            documentType: QmDocumentType.CofQualityDoc,
            productNumber: material?.product || '',
          },
        },
      })
    }
  }, [certificateContentType, getCertificateContent, material])

  useEffect(() => {
    if (productDetailData?.getProductDetail?.id) {
      getEmproveProductsDocs({
        variables: { term: productDetailData?.getProductDetail?.id as string },
      })
      getPricingForProduct({
        variables: {
          productDetails: [
            {
              productNumber: material?.product as string,
              quantity: 1,
            },
          ],
        },
      })
    }
  }, [productDetailData])

  const emproveDocuments = emproveDocumentsData?.getEmproveProductDocs

  useEffect(() => {
    try {
      setCopiedAI('')
      setAI240Missing(false)
      const parsedata: any = parseBarcode(barcode)
      setParsedValue(parsedata.parsedCodeItems)

      const lotItem: any = parsedata.parsedCodeItems.find(
        (item: any) => item.dataTitle === 'BATCH/LOT'
      )
      const gtinItem: any = parsedata.parsedCodeItems.find(
        (item: any) => item.dataTitle === 'GTIN'
      )
      const additionalId: any = parsedata.parsedCodeItems.find(
        (item: any) => item.dataTitle === 'ADDITIONAL ID'
      )
      if (lotItem) {
        setLotNumber(lotItem.data)
        setAdditionalId(additionalId.data)
      }
      if (gtinItem) {
        getGTIN({
          variables: {
            searchTerm: gtinItem.data,
            type: 'material_gtin',
          },
        })
      }
    } catch (e) {
      setParsedValue([])
      setGlobalSnackbarState({
        open: true,
        message: formatMessage({
          id: 'SCAN_NOW_ERROR',
          defaultMessage:
            'There are no valid AIs to parse. Try scanning again. If the error persists, please contact Customer Service',
        }),
        variant: SnackbarType.ErrorAlert,
      })
    }
  }, [barcode])

  const isBlueProduct =
    blueDocsManagement && material?.brand?.key.toUpperCase() === 'MM'

  const AI_240 = '(Material / Item Number)'

  const handleCopy = (ai: string, content: string) => {
    setCopiedAI(ai)
    copy(content)
  }

  const isValidDate = (date) => {
    const timestamp = Date.parse(date)
    return isNaN(timestamp) ? false : true
  }

  if (materialsLoading || certificateContentLoading || contentLoading) {
    return (
      <div className={classes.loadingWrapper}>
        <CircularProgress
          color="secondary"
          size={12}
          className="loadingSpinner"
        />
        {formatMessage({ id: 'UPDATING', defaultMessage: 'Updating...' })}
      </div>
    )
  }

  const materialPricing =
    (materialPricingData &&
      materialPricingData?.getPricingForMultipleProducts?.length &&
      materialPricingData?.getPricingForMultipleProducts[0]?.materialPricing) ||
    []

  /**
   * For this here, we don't know from the start what the document type is for the
   * material. Because of this, we initially pass in the document type as a COA
   * If this is true, it will return the documentType, if false, the documentType
   * will be returned as null, then we know that it is a COQ instead, hence, running
   * the lazy query for the second time to get all of the content info needed.
   */
  const certificateContent = certificateContentData?.getCertificateContent
    ?.documentType
    ? certificateContentData?.getCertificateContent
    : contentData?.getCertificateContent

  return (
    <>
      {parsedValue?.length ? (
        <>
          {isDesktop ? <Divider /> : null}
          <Container className={classes.container}>
            {!isDesktop ? <Divider /> : null}
            <Box mt={6}>
              {material ? (
                <ScanNowMaterialInfo
                  lotNumber={lotNumber}
                  material={material}
                  certificateContent={certificateContent}
                  isBlueProduct={isBlueProduct}
                  materialPricing={materialPricing}
                />
              ) : (
                <div>
                  <Typography
                    variant="h2"
                    component="h2"
                    className={classes.result}
                  >
                    {formatMessage({
                      id: 'RESULTS',
                      defaultMessage: 'results',
                    })}
                  </Typography>
                  <GlobalAlert severity="error" className={classes.errorAlert}>
                    <FormattedMessage
                      id="SCAN_NOW_PRODUCT_NOT_FOUND_TRY_SEARCH"
                      defaultMessage="Product not found. Try using the {link}."
                      values={{
                        link: (
                          <Linq
                            href={`${documentsSearch.index('coq')}&lotNo=${lotNumber}&productNo=${additionalId}`}
                          >
                            <FormattedMessage
                              id="DOCUMENT_SEARCH"
                              defaultMessage="document search"
                            />
                          </Linq>
                        ),
                      }}
                    />
                  </GlobalAlert>
                </div>
              )}
              <TableContainer>
                <Table className={classes.table}>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell>
                        <div className={classes.aiContainer}>
                          {formatMessage({
                            id: 'SCAN_NOW_AI',
                            defaultMessage: 'AI',
                          })}
                          <InfoIcon
                            className={classes.infoIcon}
                            onClick={() => setAiInfoOpen(true)}
                          />
                          {isAiInfoOpen ? (
                            <ScanNowAiModal
                              isAiInfoOpen={isAiInfoOpen}
                              setAiInfoOpen={() => setAiInfoOpen(false)}
                            />
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatMessage({
                          id: 'SCAN_NOW_TITLE',
                          defaultMessage: 'Title',
                        })}
                      </TableCell>
                      <TableCell>
                        {formatMessage({
                          id: 'SCAN_NOW_CONTENTS',
                          defaultMessage: 'Contents',
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {formatMessage({
                          id: 'SCAN_NOW_COPY',
                          defaultMessage: 'Copy',
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedValue.map((item: any, index) => {
                      const content =
                        typeof item.data === 'object'
                          ? isValidDate(item.data)
                            ? format(item.data, 'yyyy-MMM-dd')
                            : ''
                          : item.data
                      return (
                        <TableRow key={`AI-${index}`}>
                          <TableCell>{item.ai}</TableCell>
                          <TableCell>
                            {item.ai === AI.AI_240 ? (
                              <>
                                {item.dataTitle}
                                <p>{AI_240}</p>
                              </>
                            ) : (
                              item.dataTitle
                            )}
                          </TableCell>
                          <TableCell>
                            {item.ai === AI.AI_422 ? (
                              <Box>
                                <span
                                  className={clsx({
                                    [classes.copiedtext]: copiedAI === item.ai,
                                  })}
                                >
                                  {content}
                                </span>
                                {countryNames[content] ? (
                                  <span>{` (${countryNames[content]})`}</span>
                                ) : (
                                  ''
                                )}
                                <Box className={classes.copyNote}>
                                  {formatMessage({
                                    id: 'SCAN_NOW_COUNTRY_COPY_NOTE',
                                    defaultMessage:
                                      'Note: the copy action will take only the 3-digit number.',
                                  })}
                                </Box>
                              </Box>
                            ) : (
                              <>
                                {item.ai === AI.AI_240 && isAI240Missing
                                  ? '---> '
                                  : null}
                                <span
                                  className={clsx({
                                    [classes.copiedtext]: copiedAI === item.ai,
                                  })}
                                >
                                  {content}
                                </span>
                              </>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <CopyIcon
                              color="primary"
                              className={classes.copyIcon}
                              onClick={() => handleCopy(item.ai, content)}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            {material &&
              productDetailData?.getProductDetail?.links &&
              emproveDocuments && (
                <ScanNowLinks
                  material={material}
                  barcode={barcode}
                  productDetailData={productDetailData.getProductDetail}
                  emproveDocuments={emproveDocuments}
                  isBlueProduct={isBlueProduct}
                />
              )}
          </Container>
        </>
      ) : null}
    </>
  )
}

export default ScanNowDetail
